import "bulma/css/bulma.min.css";
import "@fortawesome/fontawesome-free/js/all";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import Dashboard from "pages/Dashboard";
import SignInForm from "./components/SignIn/SignInForm";
import ResetPasswordForm from "./components/SignIn/ResetPassword";
import firebase, { FS } from "./services/firebase";
import { fetchFirebasePreload, setAppError, setLogin, setUserId } from "./components/appSlice";
import {
  resetOtherClientAppointments,
  setArchivedClientAppointments,
  setClientAppointments,
  setHistoryClientAppointments,
  setOtherClientAppointmentsLoaded,
} from "./components/Workflow/workflowSlice";
import { setMedicalResultsByClientId } from "./components/Workflow/medicalResultsSlice";
import { setAppointmentRequests } from "./components/AppointmentRequestTable/appointmentRequestSlice";
import { getUser } from "./services/auth";
import { checkInUser, checkIsLoginStatusExpired, sendHeartbeat } from "./services/userActivities";
import { setCurrentUser } from "./state/reducers/user";
import { getUserActivityInfoFromStorage, setUserActivityInfoFromStorage, signOut } from "./utils/userActivity";
import {
  clearMfaSession,
  isMfaSessionActive,
  getMfaSessionFirebaseAuthUid,
  getMfaSessionRemainingMs,
} from "./utils/mfaSession";
import { isPermit } from "./utils";
import { CLIENT_APPOINTMENTS, FIRST_SNAPSHOT_TIMEOUT_MS, SCOPE, USER_ACTIVITY_HEARTBEAT_INTERVAL } from "./constants";
import { isManualArchiveWorkflowEnabled } from "./infra/featureToggle";
import { captureFirestoreListenerStall } from "./infra/sentry";
import {
  areArchivedAndHistoryLoaded,
  mergeAppointmentSnapshots,
  reconcileListenerAppointments,
} from "./utils/mergeAppointmentSnapshots";
import { splitClientAppointmentsByTab } from "./utils/splitClientAppointmentsByTab";

import "./App.css";
import "antd/dist/antd.css";

interface SyncedMedicalResult {
  id: string;
  clientId: string;
  clientName?: string;
  fileId?: string;
  resultType?: string;
  status?: string;
  updatedAt?: string;
}

const RETRY_BASE_DELAY_MS = 2000;
const RETRY_MAX_DELAY_MS = 30000;

// Firestore stops delivering snapshots on a listener once its error callback
// has fired, so a broken listener needs to be explicitly torn down and
// re-subscribed. Retries back off exponentially to avoid hammering Firestore
// while a connection issue persists.
function subscribeWithRetry<T = FS.DocumentData>(
  getQuery: () => FS.Query<T>,
  onData: (snapshot: FS.QuerySnapshot<T>) => void,
  onError: (error: FS.FirestoreError) => void,
  options: FS.SnapshotListenOptions = {}
): () => void {
  let retryCount = 0;
  let retryTimeout: ReturnType<typeof setTimeout> | null = null;
  let currentUnsubscribe: (() => void) | null = null;
  let cancelled = false;

  const subscribe = () => {
    if (cancelled) return;

    currentUnsubscribe = FS.onSnapshot(
      getQuery(),
      options,
      (snapshot) => {
        retryCount = 0;
        onData(snapshot);
      },
      (error) => {
        onError(error);

        if (currentUnsubscribe) {
          currentUnsubscribe();
          currentUnsubscribe = null;
        }

        retryCount++;
        const delay = Math.min(RETRY_BASE_DELAY_MS * Math.pow(2, retryCount - 1), RETRY_MAX_DELAY_MS);
        retryTimeout = setTimeout(subscribe, delay);
      }
    );
  };

  subscribe();

  return () => {
    cancelled = true;
    if (currentUnsubscribe) currentUnsubscribe();
    if (retryTimeout) clearTimeout(retryTimeout);
  };
}

const mapStateToProps = (state: any) => ({
  userId: state.app.userId,
  isLoggedIn: state.app.isLoggedIn,
  error: state.app.error,
  displayArchiveOnly: state.workflow.displayArchiveOnly,
  isOtherClientAppointmentsRequested: state.workflow.isOtherClientAppointmentsRequested,
});
const mapDispatchToProps = {
  setLogin,
  setUserId,
  setClientAppointments,
  setAppointmentRequests,
  setArchivedClientAppointments,
  setHistoryClientAppointments,
};

const App: React.FC<any> = ({
  isLoggedIn,
  setLogin,
  setUserId,
  error,
  setClientAppointments,
  setAppointmentRequests,
  setArchivedClientAppointments,
  setHistoryClientAppointments,
  displayArchiveOnly,
  isOtherClientAppointmentsRequested,
  start,
}) => {
  const dispatch = useDispatch();
  const [isValidatingUser, setIsValidatingUser] = useState(false);
  const [isPreloading, setIsPreLoading] = useState(false);
  const [signOutError, setSignOutError] = useState(null);
  const [clientAppointmentsConnectionError, setClientAppointmentsConnectionError] = useState(false);
  const [otherClientAppointmentsConnectionError, setOtherClientAppointmentsConnectionError] = useState(false);
  const [medicalResultsConnectionError, setMedicalResultsConnectionError] = useState(false);
  const [isWorkflowLoadStalled, setIsWorkflowLoadStalled] = useState(false);
  // Independent listeners share this banner; deriving it as an OR keeps
  // one listener's recovery from silently hiding another's ongoing error.
  const firestoreConnectionError =
    clientAppointmentsConnectionError || otherClientAppointmentsConnectionError || medicalResultsConnectionError;
  // Only the first auth state this component resolves can be a session carried
  // over from a previous visit; everything after it happens in front of the
  // user, mid sign in.
  const isFirstAuthResolution = useRef(true);

  const handleCheckIn = async () => {
    const { data, error } = await checkInUser();
    if (data && !error) {
      const { activityId, userActivitySessionId } = data;
      setUserActivityInfoFromStorage(activityId, userActivitySessionId);
    } else {
      await signOut();
    }
  };

  const validateAuthUser = useCallback(
    async (authUser: any) => {
      const isRestoredSession = isFirstAuthResolution.current;
      isFirstAuthResolution.current = false;

      if (!authUser) {
        setUserId(null);
        setLogin(false);
        setIsValidatingUser(false);
        return;
      }

      // Every login flow records when it verified, so a persisted Firebase
      // credential is never on its own enough to get back in - it would carry
      // no expiry of any kind.
      if (!isMfaSessionActive()) {
        setUserId(null);
        setLogin(false);
        // Showing the login form is not enough on its own: request.js mints
        // bearer tokens straight from the Firebase user, so a credential
        // carried over with no current verification has to go. Mid sign in
        // this same state is normal - the MFA step has not run yet - and the
        // rest of the flow still needs that credential, so leave it be.
        if (isRestoredSession) {
          await firebase.auth.signOut();
        }
        setIsValidatingUser(false);
        return;
      }

      if (getMfaSessionFirebaseAuthUid() !== authUser.uid) {
        clearMfaSession();
        setUserId(null);
        setLogin(false);
        await firebase.auth.signOut();
        setIsValidatingUser(false);
        return;
      }

      const uid = authUser.uid;
      const { data: user } = await getUser(uid);
      if (!isPermit(user?.role, SCOPE.LOGIN)) {
        dispatch(
          setAppError({
            debug: "unauthorized",
            message: "You are not telecare admin, please contact your telecare manager to get access!",
          })
        );
        setLogin(false);
        await firebase.auth.signOut();
        setIsValidatingUser(false);
        return;
      }
      if (user?.disabled) {
        dispatch(
          setAppError({
            debug: "unauthorized",
            message: "Your account has been disabled, please contact your telecare manager to get access!",
          })
        );
        setLogin(false);
        await firebase.auth.signOut();
        setIsValidatingUser(false);
        return;
      }
      start();
      const { userActivityId, userActivitySessionId } = getUserActivityInfoFromStorage();
      if (!userActivityId && !userActivitySessionId) {
        setUserId(uid);
        setLogin(true);
        dispatch(setCurrentUser(user));
        await handleCheckIn();
        setIsValidatingUser(false);
        return;
      }

      const { data, error } = await checkIsLoginStatusExpired(userActivityId);
      if (data && !error && !data.isExpired) {
        const { userActivityId, userActivitySessionId } = data;
        setUserId(uid);
        setLogin(true);
        dispatch(setCurrentUser(user));
        setIsValidatingUser(false);
        setUserActivityInfoFromStorage(userActivityId, userActivitySessionId);
        return;
      }

      setLogin(false);
      await signOut();
      setIsValidatingUser(false);
    },
    [dispatch, setLogin, setUserId, start]
  );

  const handleLoginComplete = useCallback(async () => {
    setIsValidatingUser(true);
    await validateAuthUser(firebase.currentUser());
  }, [validateAuthUser]);

  useEffect(() => {
    setIsValidatingUser(true);
    const listener = firebase.auth.onAuthStateChanged(async (authUser) => {
      await validateAuthUser(authUser);
    });
    return () => {
      listener();
    };
  }, [validateAuthUser]);

  // Without this the API has no way of telling a tab that is still open from
  // one whose close event never arrived, so a session left behind yesterday
  // would still look alive today and log the user straight back in.
  useEffect(() => {
    if (!isLoggedIn) return;

    let cancelled = false;
    let isEndingSession = false;

    const endExpiredSession = async () => {
      if (cancelled || isEndingSession) return;
      isEndingSession = true;
      setLogin(false);
      await signOut();
    };

    // A heartbeat cannot revive an idle session: if the tab was suspended past
    // the idle window - a sleeping laptop, a frozen background tab - the API
    // checks the session out and says so, and the tab signs out rather than
    // carry on against a session that is already gone.
    const ping = async () => {
      // Checked on every tick because the MFA session has an absolute lifetime:
      // a page that stays open and busy would otherwise heartbeat straight past
      // it and never be asked for credentials again.
      if (!isMfaSessionActive()) {
        await endExpiredSession();
        return;
      }

      const { userActivityId } = getUserActivityInfoFromStorage();
      if (!userActivityId) return;

      const { data, error } = await sendHeartbeat(userActivityId);
      if (!error && data?.isExpired) {
        await endExpiredSession();
      }
    };

    // A page restored from the back/forward cache has had its close already
    // reported and its timers frozen, so it needs a fresh activity session -
    // or a sign out, if the API expired it while the page sat in the cache.
    const revalidateOnRestore = async (event: PageTransitionEvent) => {
      if (!event.persisted) return;

      if (!isMfaSessionActive()) {
        await endExpiredSession();
        return;
      }

      const { userActivityId } = getUserActivityInfoFromStorage();
      const { data, error } = await checkIsLoginStatusExpired(userActivityId);

      if (data && !error && !data.isExpired) {
        setUserActivityInfoFromStorage(data.userActivityId, data.userActivitySessionId);
        return;
      }

      await endExpiredSession();
    };

    ping();
    const heartbeat = setInterval(ping, USER_ACTIVITY_HEARTBEAT_INTERVAL);
    window.addEventListener("pageshow", revalidateOnRestore);

    // The polling above would let a session that came within a tick of the cap
    // run a few minutes past it. This lands on the deadline itself, so the
    // stated maximum age is the real one however the page got here.
    const mfaDeadline = setTimeout(endExpiredSession, getMfaSessionRemainingMs());

    return () => {
      cancelled = true;
      clearInterval(heartbeat);
      clearTimeout(mfaDeadline);
      window.removeEventListener("pageshow", revalidateOnRestore);
    };
  }, [isLoggedIn, setLogin]);

  useEffect(() => {
    if (isLoggedIn) {
      const preload = async () => {
        setIsPreLoading(true);
        await dispatch(fetchFirebasePreload());
        setIsPreLoading(false);
      };
      preload();
    }
  }, [isLoggedIn, dispatch]);

  // The WORKFLOW tab listens to its own statuses only. Listening to the whole
  // collection made its first snapshot the size of every workflow ever kept,
  // and on a connection that drops every 20-30 seconds that snapshot never
  // finished, so the table stayed empty. Every other status comes from a
  // second listener; together they are exactly the documents the tabs used.
  //
  // The second listener starts only when a screen asks for it. The SDK runs
  // every listener over one stream and raises no snapshot until all of them
  // have loaded, so starting it at login made WORKFLOW wait for it anyway.
  const startOtherListenerRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!isLoggedIn) return;

    const clientAppointmentsCollection = FS.collection(firebase.modularFirestore(), "client_appointments");
    const toClientAppointments = (snapshot: FS.QuerySnapshot) =>
      snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));
    // What each listener last received (server or cache), and what it shows:
    // the same, except for documents held while the two are not both live.
    let workflowServerAppointments: any[] | null = null;
    let otherServerAppointments: any[] | null = null;
    let workflowClientAppointments: any[] | null = null;
    let otherClientAppointments: any[] | null = null;
    // Live: its latest snapshot is from the server, and it has not errored since.
    let isWorkflowListenerLive = false;
    let isOtherListenerLive = false;
    let unsubscribeOther: (() => void) | null = null;
    let hasOtherServerSnapshot = false;
    let isOtherLoaded = false;

    const reconcile = () => {
      const { workflow, other } = reconcileListenerAppointments({
        workflowReceived: workflowServerAppointments,
        otherReceived: otherServerAppointments,
        workflowShown: workflowClientAppointments,
        otherShown: otherClientAppointments,
        areBothLive: isWorkflowListenerLive && isOtherListenerLive,
        isOtherListenerStarted: unsubscribeOther !== null,
        isWorkflowListenerLive,
      });
      workflowClientAppointments = workflow;
      otherClientAppointments = other;
    };
    let lastFiredListener: "workflow" | "other" = "workflow";
    let publishTimeout: ReturnType<typeof setTimeout> | null = null;

    const publish = (clientAppointments: any[]) => {
      const {
        clientAppointments: activeClientAppointments,
        archivedClientAppointments,
        historyClientAppointments,
      } = splitClientAppointmentsByTab(clientAppointments, {
        moveArchivedActiveRows: isManualArchiveWorkflowEnabled,
      });
      setClientAppointments({
        clientAppointments: activeClientAppointments,
      });
      // ARCHIVED and HISTORY wait for both listeners, so they are never shown
      // half loaded.
      if (workflowClientAppointments && otherClientAppointments) {
        setArchivedClientAppointments({
          archivedClientAppointments,
        });
        setHistoryClientAppointments({
          historyClientAppointments,
        });
      }
      if (
        !isOtherLoaded &&
        areArchivedAndHistoryLoaded({
          workflowShown: workflowClientAppointments,
          otherShown: otherClientAppointments,
          hasOtherServerSnapshot,
        })
      ) {
        isOtherLoaded = true;
        dispatch(setOtherClientAppointmentsLoaded(true));
      }
    };

    // The SDK hands each listener its snapshot in a task of its own, so a
    // document moving between the two would leave one listener before the
    // other has it - gone from every tab for a render. Both callbacks of one
    // server update are queued together; publishing after them sees it whole.
    const schedulePublish = (listener: "workflow" | "other") => {
      lastFiredListener = listener;
      if (publishTimeout) return;
      publishTimeout = setTimeout(() => {
        publishTimeout = null;
        reconcile();
        const workflow = workflowClientAppointments || [];
        const other = otherClientAppointments || [];
        publish(
          lastFiredListener === "workflow"
            ? mergeAppointmentSnapshots(workflow, other)
            : mergeAppointmentSnapshots(other, workflow)
        );
      }, 0);
    };

    const stallTimeout = setTimeout(() => {
      setIsWorkflowLoadStalled(true);
      captureFirestoreListenerStall({
        listener: "client_appointments_workflow",
        databaseId: process.env.REACT_APP_FIRESTORE_ID,
        uid: firebase.currentUser()?.uid,
        timeoutMs: FIRST_SNAPSHOT_TIMEOUT_MS,
      });
    }, FIRST_SNAPSHOT_TIMEOUT_MS);

    const unsubscribeWorkflow = subscribeWithRetry(
      () => FS.query(clientAppointmentsCollection, FS.where("appointment_t.status", "in", CLIENT_APPOINTMENTS)),
      (snapshot) => {
        // A snapshot from cache can arrive empty before the server has
        // answered at all; only the server's answer means the data loaded.
        if (!snapshot.metadata.fromCache) {
          clearTimeout(stallTimeout);
          setIsWorkflowLoadStalled(false);
        }
        workflowServerAppointments = toClientAppointments(snapshot);
        // A cached snapshot - offline, or just re-subscribed - can be stale or
        // incomplete, so it does not count as live.
        isWorkflowListenerLive = !snapshot.metadata.fromCache;
        schedulePublish("workflow");
        setClientAppointmentsConnectionError(false);
      },
      () => {
        isWorkflowListenerLive = false;
        setClientAppointmentsConnectionError(true);
      },
      // Without metadata changes a server answer that changes no documents,
      // such as confirming an empty cache, would never reach the callback.
      { includeMetadataChanges: true }
    );
    startOtherListenerRef.current = () => {
      if (unsubscribeOther) return;
      unsubscribeOther = subscribeWithRetry(
        () => FS.query(clientAppointmentsCollection, FS.where("appointment_t.status", "not-in", CLIENT_APPOINTMENTS)),
        (snapshot) => {
          otherServerAppointments = toClientAppointments(snapshot);
          isOtherListenerLive = !snapshot.metadata.fromCache;
          if (isOtherListenerLive) hasOtherServerSnapshot = true;
          schedulePublish("other");
          setOtherClientAppointmentsConnectionError(false);
        },
        () => {
          isOtherListenerLive = false;
          setOtherClientAppointmentsConnectionError(true);
        },
        { includeMetadataChanges: true }
      );
    };

    return () => {
      clearTimeout(stallTimeout);
      if (publishTimeout) clearTimeout(publishTimeout);
      setIsWorkflowLoadStalled(false);
      unsubscribeWorkflow();
      startOtherListenerRef.current = null;
      if (unsubscribeOther) unsubscribeOther();
      setOtherClientAppointmentsConnectionError(false);
      dispatch(resetOtherClientAppointments());
    };
  }, [isLoggedIn, dispatch, setArchivedClientAppointments, setClientAppointments, setHistoryClientAppointments]);

  // Declared after the effect above so that, in a commit where both run, the
  // listener it sets up is already there to start.
  useEffect(() => {
    if (isLoggedIn && isOtherClientAppointmentsRequested && startOtherListenerRef.current) {
      startOtherListenerRef.current();
    }
  }, [isLoggedIn, isOtherClientAppointmentsRequested]);

  // Linked reports have `clientId`. Group once so each Results cell can
  // look up that patient's synced reports without its own query.
  useEffect(() => {
    if (!isLoggedIn) return;

    return subscribeWithRetry(
      () =>
        FS.query(
          FS.collection(firebase.modularFirestore(), "ai_extracted_medical_results"),
          FS.where("clientId", "!=", null)
        ),
      (snapshot) => {
        const resultsByClientId: Record<string, SyncedMedicalResult[]> = {};
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          const clientId: string = data.clientId;
          const rawUpdatedAt = data.updatedAt;
          const updatedAt =
            typeof rawUpdatedAt?.toDate === "function"
              ? rawUpdatedAt.toDate().toISOString()
              : rawUpdatedAt instanceof Date
              ? rawUpdatedAt.toISOString()
              : rawUpdatedAt;
          const result: SyncedMedicalResult = {
            id: doc.id,
            clientId,
            clientName: data.clientName,
            fileId: data.fileId,
            resultType: data.resultType,
            status: data.status,
            updatedAt,
          };
          if (!resultsByClientId[clientId]) {
            resultsByClientId[clientId] = [];
          }
          resultsByClientId[clientId].push(result);
        });
        dispatch(setMedicalResultsByClientId(resultsByClientId));
        setMedicalResultsConnectionError(false);
      },
      (error: FS.FirestoreError) => {
        console.error("Error fetching real-time synced medical results:", error);
        setMedicalResultsConnectionError(true);
      }
    );
  }, [isLoggedIn, dispatch]);

  const handleSignOut = async () => {
    setLogin(false);
    setSignOutError(null);
    try {
      await signOut();
    } catch (error) {
      if (error instanceof Error) {
        setSignOutError(error?.message);
      } else {
        setSignOutError(undefined);
      }
    }
  };

  const location: { state?: { isResetPassword?: boolean }; pathname?: string } = useLocation();

  const generateSign = () => {
    const isResetPassword = location?.state?.isResetPassword;
    return isResetPassword ? <ResetPasswordForm /> : <SignInForm onLoginComplete={handleLoginComplete} />;
  };

  return (
    <>
      {error && <div className="notification is-warning is-size-7 is-radiusless">{error}</div>}
      {signOutError && <div className="notification is-danger is-size-7 is-radiusless">{signOutError}</div>}
      {firestoreConnectionError && (
        <div className="notification is-warning is-size-7 is-radiusless">
          Connection to database interrupted. Reconnecting...
        </div>
      )}
      {isWorkflowLoadStalled && (
        <div className="notification is-warning is-size-7 is-radiusless">
          Workflow data is taking longer than usual to load. Please check your network connection.
        </div>
      )}
      <div className={isLoggedIn ? "container is-fluid" : ""}>
        <div className={`${isValidatingUser || isPreloading ? "is-active " : ""}modal`}>
          <div className="modal-background global" />
          <div className="modal-content">
            <div className="loading-text is-size-7">
              {isValidatingUser ? "validating user ..." : "preloading data ..."}
            </div>
            <progress className="progress is-small is-link is-radiusless" max="100" />
          </div>
        </div>
        {!isLoggedIn ? generateSign() : <Dashboard handleSignOut={handleSignOut} />}
      </div>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
